import { supabase } from './supabase';
import { createResourceWorkingAvailability } from './resourceAvailability';
import { invokeErrorMessage } from './supabaseFunctionError';

interface ResourceShift {
  start: string;
  end: string;
}

interface DaySchedule {
  enabled: boolean;
  shifts: ResourceShift[];
}

interface ResourceSchedule {
  monday: DaySchedule;
  tuesday: DaySchedule;
  wednesday: DaySchedule;
  thursday: DaySchedule;
  friday: DaySchedule;
  saturday: DaySchedule;
  sunday: DaySchedule;
}

export interface Resource {
  id: string;
  business_id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  role: string; // Legacy role column (kept for database compatibility)
  role_id?: string; // Permission role ID
  roleName?: string; // Role name from new permission system
  permission_role?: { // Permission role details
    id: string;
    name: string;
    description: string;
    permissions: any;
  };
  hourly_rate: number;
  commission_rate: number;
  status: string;
  last_active?: string; // Made optional since column doesn't exist in database
  created_at: string;
  updated_at: string;
  permissions?: ResourcePermission[];
  schedule_availability: ResourceSchedule;
  compensation_type: 'hourly' | 'flat_rate' | 'commission';
  flat_rate_amount: number;
  commission_percentage: number;
  earnings_ytd: number;
  earnings_mtd: number;
  earnings_last_month: number;
  is_mobile_available: boolean;
  not_bookable: boolean;
  auth_user_id?: string;
  is_admin: boolean;
  password?: string; // Temporary field for database triggers
  avatar_url?: string;
  bonus_configuration?: {
    booking_bonus?: {
      enabled: boolean;
      percentage: number;
    };
    service_bonus?: {
      enabled: boolean;
      services: { serviceId: string; percentage: number }[];
    };
    upsell_bonus?: {
      enabled: boolean;
      payout?: 'creator' | 'split';
      apply_to?: 'anything' | 'selected';
      percentage?: number;
      services?: { serviceId: string; percentage: number }[];
      addons?: { addonId: string; percentage: number }[];
    };
  };
  clock_in_passcode?: string; // 4-digit passcode for clocking in via URL
  location_permission?: string | null; // always | when_in_use | denied | restricted | not_determined
  location_permission_updated_at?: string | null;
}

type CreateResourcePayload = Omit<
  Resource,
  'id' | 'created_at' | 'updated_at' | 'permissions'
> & {
  password: string;
  confirm_password: string;
};

interface ResourcePermission {
  id: string;
  resource_id: string;
  permission_type: string;
  permission_action: string;
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

export async function getResources(businessId: string) {
  if (!supabase) {
    throw new Error('Supabase client is not initialized');
  }

  console.log('Fetching resources for business:', businessId);

  const { data, error } = await supabase
    .from('resources')
    .select(
      `
      *,
      permissions:resource_permissions(*),
      permission_role:resource_roles(*)
    `
    )
    .eq('business_id', businessId)
    .order('created_at', { ascending: false });

  // Debug: Log is_mobile_available field
  if (data && data.length > 0) {
    console.log('📋 Resources fetched with mobile availability:', data.map(r => ({
      id: r.id,
      name: `${r.first_name} ${r.last_name}`,
      is_mobile_available: r.is_mobile_available,
      type: typeof r.is_mobile_available
    })));
  }

  if (error) {
    console.error('Error fetching resources:', error);
    throw error;
  }

  console.log('Resources fetched:', data);
  
  // Map is_active to status for UI compatibility
  const mappedData = data?.map(resource => ({
    ...resource,
    status: resource.is_active ? 'active' : 'inactive'
  }));
  
  return mappedData as Resource[];
}

export async function getResourceServiceAreas(resourceId: string, day: string) {
  const { data, error } = await supabase
    .from('resource_service_areas')
    .select('service_area_id')
    .eq('resource_id', resourceId)
    .eq('day_of_week', day);

  if (error) throw error;
  return data;
}

async function getResource(id: string) {
  const { data, error } = await supabase
    .from('resources')
    .select(
      `
      *,
      permissions:resource_permissions(*)
    `
    )
    .eq('id', id)
    .single();

  if (error) throw error;
  return data as Resource;
}

export async function getResourceByAuthId(authUserId: string) {
  if (!authUserId) {
    console.log('No auth user ID provided');
    return null;
  }

  const { data, error } = await supabase
    .from('resources')
    .select('*')
    .eq('auth_user_id', authUserId)
    .maybeSingle();

  if (error) {
    // Only log as error if it's not a "no rows returned" error
    if (!error.message.includes('no rows returned')) {
      console.error('Error getting resource by auth ID:', {
        error,
        authUserId
      });
    } else {
      console.log('No resource found for auth ID:', authUserId);
    }
    return null;
  }

  if (!data) {
    console.log('No resource found for auth ID:', authUserId);
    return null;
  }

  return data as Resource | null;
}

export async function createResource(resource: CreateResourcePayload) {
  console.log('🔍 [DEBUG] createResource called with payload:', resource);
  
  if (!supabase) {
    console.error('❌ [DEBUG] Supabase client is not initialized');
    throw new Error('Supabase client is not initialized');
  }

  const { confirm_password, password, ...resourceData } = resource;
  console.log('🔍 [DEBUG] Calling RPC function to create resource with auth');

  // Use RPC function to create resource with auth user
  // This bypasses PostgREST schema cache issues
  const { data: newResource, error } = await supabase
    .rpc('create_resource_with_auth', {
      p_business_id: resourceData.business_id,
      p_email: resourceData.email,
      p_password: password,
      p_first_name: resourceData.first_name,
      p_last_name: resourceData.last_name,
      p_phone: resourceData.phone || null,
      p_role: resourceData.role || null,
      p_is_admin: resourceData.is_admin ?? false,
      p_hourly_rate: resourceData.hourly_rate || null,
      p_flat_rate_amount: resourceData.flat_rate_amount || null,
      p_commission_percentage: resourceData.commission_percentage || null,
      p_commission_rate: resourceData.commission_rate || null,
      p_compensation_type: resourceData.compensation_type || 'hourly'
    });

  console.log('🔍 [DEBUG] RPC result:', { data: newResource, error });

  if (error) {
    console.error('❌ [DEBUG] Error creating resource via RPC:', error);
    throw new Error(error.message || 'Failed to create team member');
  }

  if (!newResource) {
    console.error('❌ [DEBUG] No resource data returned from RPC');
    throw new Error('Resource creation failed without data.');
  }

  console.log('✅ [DEBUG] Resource created successfully via RPC:', newResource);

  // The create RPC doesn't accept the permission role — assign it now if one was chosen.
  const newResourceId = typeof newResource === 'string' ? newResource : (newResource as any)?.id;
  if (resourceData.role_id && newResourceId) {
    const { error: roleError } = await supabase
      .from('resources')
      .update({ role_id: resourceData.role_id })
      .eq('id', newResourceId);
    if (roleError) {
      console.error('⚠️ Failed to assign permission role to new resource:', roleError);
    } else if (newResource && typeof newResource === 'object') {
      (newResource as any).role_id = resourceData.role_id;
    }
  }

  // Send invite email in the background — don't block resource creation on email delivery
  supabase.functions.invoke('send-resource-invite', {
    body: {
      resourceEmail: resourceData.email,
      resourceFirstName: resourceData.first_name,
      businessId: resourceData.business_id,
    },
  }).then(({ error: inviteError }) => {
    if (inviteError) {
      console.warn('⚠️ Failed to send invite email:', inviteError.message);
    } else {
      console.log('✉️ Invite email sent to', resourceData.email);
    }
  }).catch((err) => {
    console.warn('⚠️ Invite email request failed:', err);
  });

  return newResource as Resource;
}

/** Create a resource WITHOUT an auth login (assignable to bookings; invite later). */
export async function createResourceNoAuth(resource: Omit<CreateResourcePayload, 'password' | 'confirm_password'>) {
  if (!supabase) throw new Error('Supabase client is not initialized');
  const { data, error } = await supabase.rpc('create_resource_no_auth', {
    p_business_id: resource.business_id,
    p_email: resource.email,
    p_first_name: resource.first_name,
    p_last_name: resource.last_name,
    p_phone: resource.phone || null,
    p_role: resource.role || null,
    p_role_id: resource.role_id || null,
    p_is_admin: resource.is_admin ?? false,
    p_hourly_rate: resource.hourly_rate || null,
    p_flat_rate_amount: resource.flat_rate_amount || null,
    p_commission_percentage: resource.commission_percentage || null,
    p_commission_rate: resource.commission_rate || null,
    p_compensation_type: resource.compensation_type || 'hourly',
  });
  if (error) throw new Error(error.message || 'Failed to create team member');
  let row: unknown = data;
  // PostgREST sometimes returns a `json` RPC result as a string.
  if (typeof row === 'string') {
    try { row = JSON.parse(row); } catch { /* keep the raw value */ }
  }
  return row as Resource;
}

/**
 * Link a resource to an existing auth account (e.g. the business owner's own
 * login) so no separate resource login is needed. Used for the sole-proprietor
 * "I'm also a team member" flow.
 */
export async function linkResourceToAuthUser(resourceId: string, authUserId: string) {
  if (!supabase) throw new Error('Supabase client is not initialized');
  const { data, error } = await supabase
    .from('resources')
    .update({ auth_user_id: authUserId, auth_id: authUserId })
    .eq('id', resourceId)
    .select()
    .single();
  if (error) throw error;
  return data as Resource;
}

/** Send a signup-link invite (email now; SMS when the Nexus number is configured). */
export async function inviteResource(resourceId: string): Promise<{ link?: string; emailSent?: boolean; smsSent?: boolean }> {
  if (!supabase) throw new Error('Supabase client is not initialized');
  if (!resourceId) throw new Error('Missing team member id');
  const { data, error } = await supabase.functions.invoke('invite-resource', { body: { resourceId } });
  if (error || !data?.success) {
    const msg = await invokeErrorMessage(error, data) || data?.error || 'Failed to send invite';
    throw new Error(msg);
  }
  if (!data.emailSent && !data.smsSent) {
    throw new Error('The invite was saved, but the email did not send. Try again.');
  }
  return data;
}

export async function updateResource(id: string, updates: Partial<Resource>) {
  const { permissions, status, ...resourceUpdates } = updates;

  // Map status to is_active for database
  const dbUpdates = {
    ...resourceUpdates,
    ...(status !== undefined && { is_active: status === 'active' })
  };

  const { data, error } = await supabase
    .from('resources')
    .update(dbUpdates)
    .eq('id', id)
    .select(
      `
      *,
      permissions:resource_permissions(*)
    `
    )
    .single();

  if (error) throw error;

  // Map is_active back to status for UI
  const updatedResource = {
    ...data,
    status: data.is_active ? 'active' : 'inactive'
  };

  if (permissions) {
    // First delete existing permissions
    const { error: deleteError } = await supabase
      .from('resource_permissions')
      .delete()
      .eq('resource_id', id);

    if (deleteError) throw deleteError;

    // Then insert new permissions if any exist
    if (permissions.length > 0) {
      const { error: insertError } = await supabase
        .from('resource_permissions')
        .insert(
          permissions.map((p) => ({
            resource_id: id,
            permission_type: p.permission_type,
            permission_action: p.permission_action,
            enabled: p.enabled,
          }))
        );

      if (insertError) throw insertError;
    }
  }

  return updatedResource as Resource;
}

export async function activateResource(id: string): Promise<Resource> {
  if (!supabase) {
    throw new Error('Supabase client is not initialized');
  }

  const { data, error } = await supabase
    .from('resources')
    .update({ status: 'active' })
    .eq('id', id)
    .select(
      `
      *,
      permissions:resource_permissions(*)
    `
    )
    .single();

  if (error) throw error;
  return data as Resource;
}

export async function deleteResource(id: string) {
  const { error } = await supabase.from('resources').delete().eq('id', id);

  if (error) throw error;
}

async function updateResourcePermissions(
  resourceId: string,
  permissions: Partial<ResourcePermission>[]
) {
  const { error } = await supabase.from('resource_permissions').upsert(
    permissions.map((p) => ({
      resource_id: resourceId,
      permission_type: p.permission_type,
      permission_action: p.permission_action,
      enabled: p.enabled,
    }))
  );

  if (error) throw error;
}
